import { convertToUtcTime, formatNumeric } from '../../../../util';
import moment from 'moment';
import axios from 'axios';
import {
  dateFormat_DD_MM_YY,
  TransactionProductMapping,
  TransactionTypes
} from '../../../../util/GlobalConstant';
import {
  getCodesFromLC,
  getDataFromServer,
  isEmptyNullUndef,
  setCodesJSONToLC,
  getFormattedDate
} from '../../../util';

export class TransactionHistoryModule {
  constructor(baseURL, httpHeaders) {
    this.baseURL = baseURL;
    this.httpHeaders = httpHeaders;
    this.cancelTokenSource = null;
  }

  // Method to cancel the current request
  cancelRequest = (message = 'Request canceled by user') => {
    if (this.cancelTokenSource) {
      this.cancelTokenSource.cancel(message);
    }
  };

  // Method to create a new cancel token
  createCancelToken = () => {
    // Cancel any existing request
    this.cancelRequest('New request initiated');

    // Create a new cancel token source
    this.cancelTokenSource = axios.CancelToken.source();
    return this.cancelTokenSource.token;
  };

  fetchTransactionHistory = async (payload = {}) => {
    // Cancel any existing request first
    if (this.cancelTokenSource) {
      this.cancelRequest('New request initiated');
    }

    const {
      customerKey,
      to,
      from,
      offset,
      limit,
      isScrolled,
      transactionType
    } = payload;

    // Create a new cancel token for this request
    const cancelToken = this.createCancelToken();
    const url = `${this.baseURL}/customers/${customerKey}/transactionHistory`;
    let params = {
      to: convertToUtcTime(
        to
          ? `${to} 23:59:59.999`
          : `${moment().format('DD/MM/YYYY')} 23:59:59.999`
      ),
      from: convertToUtcTime(
        from
          ? `${from} 00:00:00.000`
          : `${moment()
              .subtract(1, 'months')
              .add(1, 'days')
              .format('DD/MM/YYYY')} 00:00:00.000`
      ),
      limit: limit || 20,
      offset: offset || 0
    };

    // if payload has filter
    if (payload.filter) {
      params = {
        ...payload.filter,
        to: convertToUtcTime(
          to
            ? `${to} 23:59:59.999`
            : payload.filter?.to
            ? `${payload.filter.to} 23:59:59.999`
            : `${moment().format('DD/MM/YYYY')} 23:59:59.999`
        ),
        from: convertToUtcTime(
          from
            ? `${from} 00:00:00.000`
            : payload.filter?.from
            ? `${payload.filter.from} 00:00:00.000`
            : `${moment()
                .subtract(1, 'months')
                .add(1, 'days')
                .format('DD/MM/YYYY')} 00:00:00.000`
        ),
        limit: limit || 20,
        offset: offset || 0
      };
    }

    if (!isEmptyNullUndef(transactionType)) {
      params = {
        ...params,
        transactionType
      };
    }

    const data = await getDataFromServer(
      url,
      this.httpHeaders,
      params,
      cancelToken
    );
    if (data) {
      const { transactions, count } = data;
      if (Array.isArray(transactions)) {
        const formattedTransactions = transactions.map((o) => {
          const {
            localCurrencyEquivalent,
            isCredit,
            transactionDate,
            destCountryCode,
            accountId,
            amount,
            transactionTypeDescription,
            creditLimit,
            transactionCurrencyAmount,
            accountCurrency,
            transactionCurrency
          } = o;

          return {
            ...o,
            destination: destCountryCode,
            source: `${accountId}`,
            type: transactionTypeDescription,
            credit_debit: isCredit != null ? (isCredit ? 'CR' : 'DR') : null,
            amount: amount
              ? `$${formatNumeric(Number(amount).toFixed(2))}`
              : '-',
            accountCurrency: amount ? accountCurrency : '-',
            transactionCurrency: amount ? transactionCurrency : '-',
            date: getFormattedDate(transactionDate, dateFormat_DD_MM_YY),
            creditLimit: `$${formatNumeric(Number(creditLimit).toFixed(2))}`,
            transactionCurrencyAmount: transactionCurrencyAmount
              ? `$${formatNumeric(
                  Number(transactionCurrencyAmount).toFixed(2)
                )}`
              : '-',
            localCurrencyEquivalent: `$${formatNumeric(
              Number(localCurrencyEquivalent).toFixed(2)
            )}`
          };
        });

        return {
          associated_transactions: formattedTransactions,
          isScrolled,
          customerId: customerKey,
          count
        };
      }
    }

    return {
      associated_transactions: [],
      isScrolled,
      customerId: customerKey
    };
  };

  fetchTransactionProductMapping = async () => {
    const transactionProductMappingData = [];
    const data = getCodesFromLC(TransactionProductMapping);
    if (data.length === 0) {
      const params = {
        codeType: 'TRANSACTION_PRODUCT_MAPPING'
      };
      const productMapResponse = await getDataFromServer(
        `${this.baseURL}/codes`,
        this.httpHeaders,
        params
      );
      await setCodesJSONToLC(TransactionProductMapping, productMapResponse);
      return productMapResponse;
    }
    return transactionProductMappingData;
  };

  fetchTransactionTypes = async () => {
    const transactionTypesData = [];
    const data = getCodesFromLC(TransactionTypes);
    if (data.length === 0) {
      const params = {
        codeType: 'TRANSACTION_TYPE'
      };
      const transactionTypesResponse = await getDataFromServer(
        `${this.baseURL}/codes`,
        this.httpHeaders,
        params
      );
      await setCodesJSONToLC(TransactionTypes, transactionTypesResponse);
      return transactionTypesResponse;
    }
    return transactionTypesData;
  };
}
